import { useReducer, createContext, useContext, Context } from 'react';
import {
    FormDispatch,
    FormDispatchTypes,
    FormProviderComponent,
    FormReducer,
    FormState,
    UseFormDispatchFunction,
    UseFormStateFunction,
} from './form.types';
import { isElectron } from '../../helpers/EnvironmentFlags/EnvironmentFlags';

/**
 * The form context reducer that takes dispatch actions and ensures they update
 * the current state.
 *
 * @param state - {@link FormReducer state} description.
 * @param action - {@link FormReducer action} description.
 */
export const formReducer: FormReducer = (state, action) => {
    switch (action.type) {
        case FormDispatchTypes.SetFullWidthKioskInputs:
            return {
                ...state,
                fullWidthKioskInputs: action.payload,
            };
        default:
            return state;
    }
};

const FormStateContext: Context<FormState> = createContext<FormState>(undefined);
const FormDispatchContext: Context<FormDispatch> = createContext<FormDispatch>(undefined);

/**
 * The form provider that ensures entire form controls are set to full width sizing or not.
 *
 * @param fullWidthKioskInputs - {@link FormProviderComponent fullWidthKioskInputs} description.
 * @param children - {@link FormProviderComponent children} description.
 */
export const FormProvider: FormProviderComponent = ({
    fullWidthKioskInputs = false,
    children,
}) => {
    const [state, dispatch] = useReducer<FormReducer>(formReducer, {
        fullWidthKioskInputs: fullWidthKioskInputs && isElectron(),
    });

    return (
        <FormStateContext.Provider value={state}>
            <FormDispatchContext.Provider value={dispatch}>
                {children}
            </FormDispatchContext.Provider>
        </FormStateContext.Provider>
    );
};

/**
 * Form context hook to retrieve the current state.
 */
export const useFormState: UseFormStateFunction = () => {
    const context: FormState = useContext(FormStateContext);

    if (context === undefined) {
        throw new Error('useFormState must be used within a FormProvider');
    }

    return context;
};

/**
 * Form context hook to dispatch actions to update the current state.
 */
export const useFormDispatch: UseFormDispatchFunction = () => {
    const context: FormDispatch = useContext(FormDispatchContext);

    if (context === undefined) {
        throw new Error('useFormDispatch must be used within a FormProvider');
    }

    return context;
};